import React, { useEffect, useState } from 'react';
import { RefreshCw, Sparkles, X } from 'lucide-react';

const CURRENT_BUILD_HASH = import.meta.env.VITE_BUILD_HASH || import.meta.env.VITE_BUILD_TIME || 'dev';
const LAST_RELOAD_KEY = 'yaseero_last_applied_build_hash';
const LAST_RELOAD_TS = 'yaseero_last_auto_reload_ts';

const DeploymentAutoRefresh = () => {
    const [hasUpdate, setHasUpdate] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [remoteHash, setRemoteHash] = useState(null);

    useEffect(() => {
        let isChecking = false;

        const executeCleanReload = async (targetHash, reason) => {
            if (updating) return;

            // 1. Prevent infinite reload loops (within 8 seconds or if build hash already applied)
            const now = Date.now();
            const lastTs = sessionStorage.getItem(LAST_RELOAD_TS);
            if (lastTs && now - Number(lastTs) < 8000) {
                console.warn('[DeploymentAutoRefresh] Reload loop prevented. Last reload was < 8s ago.');
                return;
            }

            const appliedHash = localStorage.getItem(LAST_RELOAD_KEY);
            if (targetHash && appliedHash === targetHash) {
                console.warn(`[DeploymentAutoRefresh] Hash ${targetHash} already applied. Reload skipped.`);
                return;
            }

            console.log(`[DeploymentAutoRefresh] Applying update (${reason}). Clearing caches & reloading...`);
            setUpdating(true);
            sessionStorage.setItem(LAST_RELOAD_TS, now.toString());

            if (targetHash) {
                localStorage.setItem(LAST_RELOAD_KEY, targetHash);
            }

            try {
                // Service Worker & Cache Storage cleanup
                if ('serviceWorker' in navigator) {
                    const registration = await navigator.serviceWorker.getRegistration();
                    if (registration) {
                        await registration.update();
                        if (registration.waiting) {
                            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
                        }
                    }
                }

                if ('caches' in window) {
                    const keys = await caches.keys();
                    await Promise.all(keys.map(k => caches.delete(k)));
                }
            } catch (err) {
                console.error('[DeploymentAutoRefresh] Error during update cleanup:', err);
            } finally {
                setTimeout(() => {
                    window.location.reload(true);
                }, 600);
            }
        };

        const checkServerVersion = async () => {
            // Skip version checking in local development if hash is unassigned
            if (import.meta.env.DEV && !import.meta.env.VITE_BUILD_HASH) return;

            if (isChecking) return;
            isChecking = true;

            try {
                const res = await fetch(`/version.json?t=${Date.now()}`, {
                    cache: 'no-store',
                    headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache' }
                });

                if (res.ok) {
                    const data = await res.json();
                    if (data && data.buildHash && data.buildHash !== CURRENT_BUILD_HASH) {
                        const appliedHash = localStorage.getItem(LAST_RELOAD_KEY);
                        if (appliedHash !== data.buildHash) {
                            const isLoginPage = window.location.pathname.includes('/login') || window.location.pathname === '/';
                            if (isLoginPage) {
                                console.log('[DeploymentAutoRefresh] Login page version update detected. Mandatory auto-refreshing...');
                                executeCleanReload(data.buildHash, 'Mandatory login-time build refresh');
                                return;
                            }

                            setRemoteHash(data.buildHash);
                            setHasUpdate(true);

                            // Trigger Service Worker update check in background
                            if ('serviceWorker' in navigator) {
                                navigator.serviceWorker.getRegistration().then(reg => reg?.update().catch(() => { }));
                            }
                        }
                    }
                }
            } catch (err) {
                // Ignore transient network errors
            } finally {
                isChecking = false;
            }
        };

        // 1. Initial check on mount
        checkServerVersion();

        // 2. Poll every 30 seconds for quick PWA update discovery
        const intervalId = setInterval(checkServerVersion, 30 * 1000);

        // 3. Re-check on visibility & focus
        const handleFocusOrVisibility = () => {
            if (document.visibilityState === 'visible') {
                checkServerVersion();
            }
        };

        window.addEventListener('visibilitychange', handleFocusOrVisibility);
        window.addEventListener('focus', handleFocusOrVisibility);
        window.addEventListener('online', checkServerVersion);

        // 4. Listen for Service Worker version update message
        const handleSwMessage = (event) => {
            if (event.data && event.data.type === 'SW_VERSION_UPDATED') {
                setHasUpdate(true);
            }
        };

        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('message', handleSwMessage);
        }

        // 5. Emergency Recovery for Chunk Loading Errors (404 JS Chunk on new deployment)
        const handleChunkError = (event) => {
            const errorMsg = (event?.message || event?.reason?.message || event?.reason || '').toString();
            if (/loading chunk|dynamically imported module|failed to fetch|import.*module/i.test(errorMsg)) {
                console.warn('[DeploymentAutoRefresh] Chunk load failure detected. Performing immediate recovery...');
                executeCleanReload(remoteHash, 'Chunk load failure recovery');
            }
        };

        window.addEventListener('error', handleChunkError);
        window.addEventListener('unhandledrejection', handleChunkError);

        return () => {
            clearInterval(intervalId);
            window.removeEventListener('visibilitychange', handleFocusOrVisibility);
            window.removeEventListener('focus', handleFocusOrVisibility);
            window.removeEventListener('online', checkServerVersion);
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.removeEventListener('message', handleSwMessage);
            }
            window.removeEventListener('error', handleChunkError);
            window.removeEventListener('unhandledrejection', handleChunkError);
        };
    }, [updating, remoteHash]);

    const handleUserApplyUpdate = async () => {
        setUpdating(true);
        try {
            if ('serviceWorker' in navigator) {
                const regs = await navigator.serviceWorker.getRegistrations();
                for (const reg of regs) {
                    if (reg.active) reg.active.postMessage({ type: 'CLEAR_ALL_CACHES' });
                    await reg.unregister();
                }
            }
            if ('caches' in window) {
                const keys = await caches.keys();
                await Promise.all(keys.map(k => caches.delete(k)));
            }
            localStorage.removeItem(LAST_RELOAD_KEY);
            sessionStorage.removeItem(LAST_RELOAD_TS);
        } catch (err) {
            console.warn('[DeploymentAutoRefresh] Error during update apply:', err);
        } finally {
            setTimeout(() => {
                const currentUrl = new URL(window.location.href);
                currentUrl.searchParams.set('pwa_refresh', Date.now().toString());
                window.location.href = currentUrl.toString();
            }, 300);
        }
    };

    if (updating) {
        return (
            <div className="fixed inset-0 z-[9999] bg-slate-900/80 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200">
                <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-2xl border border-slate-200 dark:border-slate-700 max-w-sm w-full text-center space-y-4">
                    <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-500 flex items-center justify-center mx-auto animate-spin">
                        <RefreshCw size={24} />
                    </div>
                    <div>
                        <h3 className="font-extrabold text-base text-slate-900 dark:text-white">
                            Updating Application
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                            Loading latest version build...
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (!hasUpdate) return null;

    return (
        <div className="fixed top-4 right-4 z-[9999] max-w-sm w-full bg-slate-900 text-white p-3.5 rounded-2xl shadow-2xl border border-slate-700/80 flex items-center justify-between gap-3 animate-in fade-in slide-in-from-top-4 duration-300">
            <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-xl bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 flex items-center justify-center shrink-0">
                    <Sparkles size={18} />
                </div>
                <div className="min-w-0">
                    <p className="text-xs font-black text-white truncate">New version available</p>
                    <p className="text-[10px] text-slate-300 truncate">Please refresh to load the latest version</p>
                </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                <button
                    onClick={handleUserApplyUpdate}
                    className="bg-cyan-500 hover:bg-cyan-600 text-white font-bold text-xs px-3 py-1.5 rounded-xl transition-all shadow-md active:scale-95 flex items-center gap-1.5 cursor-pointer"
                >
                    <RefreshCw size={12} />
                    Refresh
                </button>
                <button
                    onClick={() => setHasUpdate(false)}
                    className="p-1 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors cursor-pointer"
                    title="Dismiss"
                >
                    <X size={14} />
                </button>
            </div>
        </div>
    );
};

export default DeploymentAutoRefresh;
